/* eslint-disable jsx-a11y/label-has-for */
// The <label>s are lifted verbatim from the CRM's own cancel modal, which has the same exemption.
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Button, Form, Modal, TextArea } from 'semantic-ui-react';

import { namespaces, TRANSLATION_FILES } from '../constants/translation';
import { OPPORTUNITY_STATUS } from '../constants/opportunityStatuses';
import {
  CANCELLATION_CATEGORY_BUYER,
  CANCELLATION_CATEGORY_ERROR,
  cancellationCategoryForChoice,
  cancellationCategoryForReasonID,
  cancellationCategoryOptionValue,
  cancellationReasonIDForChoice,
  visibleCancellationReasons,
} from '../utils/cancellationReasonCategory';
import { isVietnamCompany } from '../utils/vietnam';

export const CANCELLATION_REASON_MAX_LENGTH = 500;

// CPI-1226: the two fixed cancellation scenarios. `value` is the wire value sent as
// sr_cancellation_type (from utils/cancellationReasonCategory); `label` doubles as the i18n key.
export const CANCELLATION_CATEGORIES = [
  { value: CANCELLATION_CATEGORY_BUYER, label: 'Cancellation due to Buyer' },
  { value: CANCELLATION_CATEGORY_ERROR, label: 'Cancellation due to Error' },
];

// Look-up for a stored cancellation; '' for an absent or unrecognised value.
export const cancellationCategoryLabel = value =>
  (CANCELLATION_CATEGORIES.find(category => category.value === value) || {}).label || '';

// Lifted out of OpportunityOverviewDetail.jsx so the CRM screen and Sales Overview share one modal.
class OpportunityCancelModal extends Component {
  static propTypes = {
    open: PropTypes.bool,
    srCode: PropTypes.string,
    onClose: PropTypes.func,
    onSubmit: PropTypes.func,
    // The company's cancellation reason master list, already in the store (BookApp.jsx fetches it).
    cancellationReasons: PropTypes.shape({
      data: PropTypes.array,
    }),
    // CPI-1226 post-fix: only to decide which entries the dropdown offers.
    auth: PropTypes.shape({
      company: PropTypes.shape({
        countryCode: PropTypes.string,
      }),
    }),
    t: PropTypes.func,
  }
  static mapStateToProps = state => ({
    cancellationReasons: state.cancellationReasons,
    auth: state.auth,
  })
  constructor(props) {
    super(props);
    this.state = {
      // CPI-1226 post-fix: what the Vietnamese dropdown holds - either a synthesized category
      // option or a master-list row id. Malaysia does not use it; it keeps the two selects.
      cancellationChoice: '',
      cancellationReasonID: '',
      cancellationCategory: '',
      cancellationReason: '',
    };
  }
  // CPI-1226 post-fix: filtered to the rows this country may offer - one in Vietnam, the whole
  // list everywhere else - so the dropdown, the free text it fills in and the category it sets all
  // read the same rows.
  reasonList = () => {
    const { auth, cancellationReasons } = this.props;
    return visibleCancellationReasons(
      auth,
      (cancellationReasons && cancellationReasons.data) || [],
    );
  }
  // CPI-1226 (PO, 5 Sep): picking a tenant reason pre-selects the category; the rule lives in
  // utils/cancellationReasonCategory. It never clears a chosen category nor overwrites typed text.
  handleExistingReasonChange = (e, { value }) => {
    const reasons = this.reasonList();
    // Sliced like handleReasonChange caps typing, or the box arrives already invalid.
    const reasonText = id => String(
      (reasons.find(reason => reason && reason.id === id) || {}).name || '',
    ).slice(0, CANCELLATION_REASON_MAX_LENGTH);
    const pickedName = reasonText(value);
    const category = cancellationCategoryForReasonID(reasons, value);
    this.setState((state) => {
      // The free text is filled in for them only while it is untouched.
      const textIsUntouched = !state.cancellationReason.trim() ||
        (!!state.cancellationReasonID &&
          state.cancellationReason === reasonText(state.cancellationReasonID));
      return {
        cancellationReasonID: value,
        cancellationCategory: category || state.cancellationCategory,
        cancellationReason: textIsUntouched ? pickedName : state.cancellationReason,
      };
    });
  }
  // CPI-1226 post-fix: Vietnam's single dropdown. A synthesized category option carries the
  // category in its own value, so picking one sets sr_cancellation_type directly and refers to no
  // master-list row; picking the developer row sets the category the way it always did and fills
  // the free text with the row's name. Switching from the row to a category clears that prefill
  // (a category has no name to copy), which puts Submit back to dead until the reason is written.
  handleChoiceChange = (e, { value }) => {
    const reasons = this.reasonList();
    const reasonText = id => String(
      (reasons.find(reason => reason && reason.id === id) || {}).name || '',
    ).slice(0, CANCELLATION_REASON_MAX_LENGTH);
    const reasonID = cancellationReasonIDForChoice(value);
    const category = cancellationCategoryForChoice(reasons, value);
    const pickedName = reasonText(reasonID);
    this.setState((state) => {
      const textIsUntouched = !state.cancellationReason.trim() ||
        (!!state.cancellationReasonID &&
          state.cancellationReason === reasonText(state.cancellationReasonID));
      return {
        cancellationChoice: value,
        cancellationReasonID: reasonID,
        cancellationCategory: category || state.cancellationCategory,
        cancellationReason: textIsUntouched ? pickedName : state.cancellationReason,
      };
    });
  }
  handleCategoryChange = (e, { value }) => {
    this.setState({ cancellationCategory: value });
  }
  handleReasonChange = (e) => {
    const inputValue = e.target.value;
    if (inputValue.length <= CANCELLATION_REASON_MAX_LENGTH) {
      this.setState({ cancellationReason: inputValue });
    }
  }
  handleClose = () => {
    this.setState({
      cancellationChoice: '',
      cancellationReasonID: '',
      cancellationCategory: '',
      cancellationReason: '',
    });
    if (this.props.onClose) this.props.onClose();
  }
  handleSubmit = () => {
    const { onSubmit } = this.props;
    const { cancellationCategory, cancellationReason } = this.state;
    if (onSubmit && cancellationCategory && cancellationReason.trim()) {
      onSubmit(
        OPPORTUNITY_STATUS.PENDING_CANCELLATION,
        cancellationReason.trim(),
        cancellationCategory,
      );
    }
    this.handleClose();
  }
  render() {
    const { auth, open, srCode, t } = this.props;
    const {
      cancellationChoice,
      cancellationReasonID,
      cancellationCategory,
      cancellationReason,
    } = this.state;
    const isVietnam = isVietnamCompany(auth);
    const characterCount = cancellationReason.length;
    const isInvalid = !cancellationCategory ||
      characterCount === 0 ||
      characterCount > CANCELLATION_REASON_MAX_LENGTH;
    const categoryOptions = CANCELLATION_CATEGORIES.map(({ value, label }) => ({
      key: value,
      value,
      text: t(label, { ns: TRANSLATION_FILES.translation }),
    }));
    // Admin-editable rows: name is the label, id is the value; hidden when the company has none.
    const existingReasonOptions = this.reasonList()
      .map(reason => ({ key: reason.id, value: reason.id, text: reason.name }));
    // CPI-1226 post-fix: Vietnam gets ONE dropdown of exactly three entries - the two categories
    // above, then the tenant's existing "Cancelled by Developer" row. Malaysia keeps the two
    // selects it has, with the same options in the same order.
    const vnChoiceOptions = categoryOptions.map(option => ({
      ...option,
      key: `category-${option.value}`,
      value: cancellationCategoryOptionValue(option.value),
    })).concat(existingReasonOptions);
    return (
      <Modal
        open={open}
        onClose={this.handleClose}
        size="small"
        className="cancellation-reason-modal"
        closeIcon
      >
        <Modal.Header className="modal-title">
          {t('Request cancellation', { ns: TRANSLATION_FILES.translation })}
        </Modal.Header>
        <Modal.Content className="modal-body-content">
          <Form>
            <Form.Field className="field-booking-id">
              <label>{t('Booking ID', { ns: TRANSLATION_FILES.translation })}</label>
              <div className="read-only-box">
                {srCode || '-'}
              </div>
            </Form.Field>

            {!isVietnam && existingReasonOptions.length > 0 &&
              <Form.Select
                label={
                  <label>
                    {t('Existing cancellation reason', { ns: TRANSLATION_FILES.translation })}
                  </label>
                }
                options={existingReasonOptions}
                value={cancellationReasonID}
                onChange={this.handleExistingReasonChange}
                placeholder={t('Select an existing reason (optional)', { ns: TRANSLATION_FILES.translation })}
                selectOnBlur={false}
              />
            }

            <Form.Select
              label={
                <label>
                  {t('Cancellation category', { ns: TRANSLATION_FILES.translation })} <span className="required-star">*</span>
                </label>
              }
              options={isVietnam ? vnChoiceOptions : categoryOptions}
              value={isVietnam ? cancellationChoice : cancellationCategory}
              onChange={isVietnam ? this.handleChoiceChange : this.handleCategoryChange}
              placeholder={t('Select a cancellation category', { ns: TRANSLATION_FILES.translation })}
              selectOnBlur={false}
            />

            <Form.Field>
              <label>
                {t('Cancellation reason', { ns: TRANSLATION_FILES.translation })} <span className="required-star">*</span>
              </label>
              <TextArea
                rows={4}
                value={cancellationReason}
                onChange={this.handleReasonChange}
                placeholder={t('Enter the reason for cancelling this booking...', { ns: TRANSLATION_FILES.translation })}
              />
              <div className={`char-counter ${characterCount > CANCELLATION_REASON_MAX_LENGTH ? 'error-limit' : ''}`}>
                {`${characterCount} / ${CANCELLATION_REASON_MAX_LENGTH}`}
              </div>
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions className="modal-actions-footer">
          <Button
            className="btn-dismiss"
            onClick={this.handleClose}
          >
            {t('Cancel', { ns: TRANSLATION_FILES.translation })}
          </Button>
          <Button
            className="btn-submit"
            onClick={this.handleSubmit}
            disabled={isInvalid}
          >
            {t('Submit request', { ns: TRANSLATION_FILES.translation })}
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

export default withTranslation(namespaces)(
  connect(OpportunityCancelModal.mapStateToProps)(OpportunityCancelModal),
);



// WEBPACK FOOTER //
// ./src/components/OpportunityCancelModal.jsx